/** @jsxImportSource @emotion/react */
import { FC, useState } from 'react'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import { Link } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import { findAllPlatforms_findAllPlatforms_items as PlatformType } from '__generatedTypes__/findAllPlatforms'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { PlatformSlug } from 'constants/consts'
import { PlatformCategory } from '__generatedTypes__/globalTypes'
import { useAuthContext } from 'utils/auth'
import { MARKETING_GROUP } from 'utils/services/auth'
import { getFullPlatformTitle, getPlatformDefaultRoute } from 'platformConfig'
import { ReactComponent as CheckmarkIcon } from '../icons/check.svg'
import { ReactComponent as ChevronTopIcon } from '../icons/chevron-top.svg'
import * as classes from './styles'

interface Props {
  selectedPlatformKey: string
  platforms: Array<PlatformType & { key: string }>
  platformCategory: string
  closeMenu: () => void
}

export const getPlatformCategoryTitle = (platformCategory: PlatformCategory) => {
  switch (platformCategory) {
    case PlatformCategory.ApplyingThisCycle:
      return 'Applying This Cycle?'
    case PlatformCategory.DentalHygiene:
      return 'Dental Hygiene'
    case PlatformCategory.GraduateExams:
      return 'Graduate Exams'
  }
  return platformCategory
}

export { getFullPlatformTitle } from 'platformConfig'

const PlatformList: FC<Props> = ({ selectedPlatformKey, platforms, platformCategory, closeMenu }) => {
  const { getLink } = useLink()
  const { groups } = useAuthContext()
  const apolloClient = useApolloClient()

  const hasSelectedPlatform = platforms.some(({ key }) => key === selectedPlatformKey)

  const [isOpen, setIsOpen] = useState<boolean>(hasSelectedPlatform)
  const handleToggleVisibility = () => setIsOpen((prev) => !prev)

  const getPlatformLink = (platformSlug: PlatformSlug) => {
    if (groups.includes(MARKETING_GROUP)) {
      return getLink(cmsRoutes.USER_LIST, { platform: platformSlug })
    }

    return getLink(getPlatformDefaultRoute(platformSlug), { platform: platformSlug })
  }

  return (
    <div css={classes.wrapper}>
      <div css={classes.category} onClick={handleToggleVisibility}>
        {getPlatformCategoryTitle(platformCategory as PlatformCategory)}
        <button css={classes.togglePlatformsButton}>
          <ChevronTopIcon css={[classes.chevron, isOpen && classes.rotatedChevron]} />
        </button>
      </div>
      <Collapse in={isOpen}>
        <ul css={classes.list}>
          {platforms.map(({ id, key, slug, title, description }) => (
            <li key={id} css={classes.li}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                to={getPlatformLink(slug as PlatformSlug)}
                css={[classes.link, key === selectedPlatformKey && classes.disabledLink]}
                onClick={() => {
                  apolloClient.clearStore()
                  closeMenu()
                }}
              >
                <div css={classes.platformInfo}>
                  <span css={classes.title}>{getFullPlatformTitle(title)}</span>
                  <Typography css={classes.description}>{description}</Typography>
                </div>
                {key === selectedPlatformKey && (
                  <span css={classes.checkmarkWrapper}>
                    <CheckmarkIcon css={classes.checkmark} />
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </Collapse>
    </div>
  )
}

export default PlatformList
